import dayjs from "dayjs";

/** Возвращает строку в формате DD.MM.YYYY HH:mm:ss
 * 
 * @param {string|Dayjs} value Дата время
 * @returns {string} Текстовое представление даты времени
 */
export const fmtDateTimeFull = (value) => {
  return value && dayjs(value).format("DD.MM.YYYY HH:mm:ss");
}

export const fmtTime = (value) => {
  return value && dayjs(value).format("HH:mm:ss");
}

export const fmtTimeHM = (value) => {
  return value && dayjs(value).format("HH:mm");
}

// Преобразование строки вида HH:mm:ss в объекта dayjs
export const cnvString2Time = (value) => {
  return value && dayjs(value, "HH:mm:ss", true);
}

export const fmtDate = (value) => {
  return value && dayjs(value).format("DD.MM.YYYY");
}

export const strDate2Server = (value) => {
  if (!value) return null;
  return dayjs(value).format("YYYY-MM-DD");
}

export const getSortedUniqueValues = (items, key, options = {}) => {
  if (!Array.isArray(items)) return [];

  const {
    formatFn = v => v,
    valueFn = v => v,
    type = "string"
  } = options;

  const seen = new Map();

  for (const item of items) {
    const raw = item?.[key];
    if (raw === undefined || raw === null) continue;

    const value = valueFn(raw);
    const text = formatFn(raw);

    if (!seen.has(value)) {
      seen.set(value, { text, value });
    }
  }

  const array = Array.from(seen.values());

  if (type === "date") {
    array.sort((a, b) => new Date(a.value) - new Date(b.value));
  } else if (type === "number") {
    array.sort((a, b) => a.value - b.value);
  } else {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    array.sort((a, b) => {
      const va = a?.value != null ? String(a.value) : '';
      const vb = b?.value != null ? String(b.value) : '';
      return collator.compare(va, vb);
    });
  }

  return array;
};

export const getDateMonthStart = (month, year) => dayjs(new Date(year, month - 1, 1));

export const getDateMonthFinish = (month, year) => getDateMonthStart(month, year).endOf('month');

// Получение номера дня недели, где 1 - Понедельник, 7 - Воскресенье
export const getDayOfTheWeek = (date) => date.day() === 0 ? 7 : date.day();

export const ftmPrice = value => value && value.toFixed(2)

export const frmSymaNull = value => value ? value.toLocaleString('ru-RU') : null;

export const fmtSyma = value => Number(value || 0)
  .toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  .replace(/[\u00A0\u202F]/g, " ");

export const isNullOrUndef = value => value === null || value === undefined;

/**
 * Конвертирует указанные поля объекта в дробные числа если возможно
 * @param {Object} obj - Исходный объект
 * @param {string[]} fields - Массив названий полей для конвертации
 * @returns {Object} - Модифицированный объект
 */
export const convertFieldsToFloat = (obj, fields) => {
  if (!obj || typeof obj !== 'object' || !fields || !Array.isArray(fields)) {
    return obj;
  }

  // Создаем копию объекта чтобы не мутировать исходный
  const result = { ...obj };

  fields.forEach(field => {
    if (result.hasOwnProperty(field)) {
      const value = result[field];
      
      // Пропускаем null и undefined
      if (value === null || value === undefined) {
        return;
      }
      
      // Если уже число - оставляем как есть
      if (typeof value === 'number') {
        return;
      }
      
      // Если строка - пытаемся преобразовать
      if (typeof value === 'string') {
        const trimmedValue = value.trim();
        
        // Пропускаем пустые строки
        if (trimmedValue === '') {
          result[field] = null;
          return;
        }
        
        // Пробуем преобразовать в число
        const numberValue = parseFloat(trimmedValue);
        
        // Если преобразование успешно и это не NaN
        if (!isNaN(numberValue) && isFinite(numberValue)) {
          result[field] = numberValue;
        }
        // Дополнительная обработка для дробей в виде строк "a/b"
        else if (trimmedValue.includes('/')) {
          const parts = trimmedValue.split('/');
          if (parts.length === 2) {
            const numerator = parseFloat(parts[0]);
            const denominator = parseFloat(parts[1]);
            
            if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
              result[field] = numerator / denominator;
            }
          }
        }
      }
    }
  });

  return result;
};

/**
 * Блокирует ввод любых символов, кроме цифр и системных клавиш
 */
export const ONLY_NUMBERS_PARSER = value => value ? value.replace(/[^\d]/g, '') : '';

export const handleOnlyNumbersKeyDown = (e) => {
  const isControlKey = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key);
  const isModifier = e.ctrlKey || e.metaKey;
  const isNumber = /[0-9]/.test(e.key);

  if (!isNumber && !isControlKey && !isModifier) {
    e.preventDefault();
  }
};

/**
 * Конвертирует указанные поля объекта в целые числа если возможно
 * @param {Object} obj - Исходный объект
 * @param {string[]} fields - Массив названий полей для конвертации
 * @returns {Object} - Модифицированный объект
 */
export const convertFieldsToInt = (obj, fields) => {
  if (!obj || typeof obj !== 'object' || !fields || !Array.isArray(fields)) {
    return obj;
  }

  // Создаем копию объекта чтобы не мутировать исходный
  const result = { ...obj };

  fields.forEach(field => {
    if (result.hasOwnProperty(field)) {
      const value = result[field];
      
      // Пропускаем null и undefined
      if (value === null || value === undefined) {
        return;
      }
      
      // Если уже целое число - оставляем как есть
      if (Number.isInteger(value)) {
        return;
      }
      
      // Если число с плавающей точкой - округляем
      if (typeof value === 'number') {
        result[field] = Math.trunc(value);
        return;
      }
      
      // Если строка - пытаемся преобразовать
      if (typeof value === 'string') {
        const trimmedValue = value.trim();
        
        // Пропускаем пустые строки
        if (trimmedValue === '') {
          result[field] = null;
          return;
        }
        
        // Пробуем преобразовать в целое число
        const numberValue = parseInt(trimmedValue, 10);
        
        // Если преобразование успешно и это не NaN
        if (!isNaN(numberValue) && isFinite(numberValue)) {
          result[field] = numberValue;
        }
        // Дополнительная обработка для дробей в виде строк "a/b"
        else if (trimmedValue.includes('/')) {
          const parts = trimmedValue.split('/');
          if (parts.length === 2) {
            const numerator = parseInt(parts[0], 10);
            const denominator = parseInt(parts[1], 10);
            
            if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
              result[field] = Math.trunc(numerator / denominator);
            }
          }
        }
        // Обработка чисел с плавающей точкой в строках
        else if (trimmedValue.includes('.')) {
          const floatValue = parseFloat(trimmedValue);
          if (!isNaN(floatValue) && isFinite(floatValue)) {
            result[field] = Math.trunc(floatValue);
          }
        }
      }
      
      // Булевы значения преобразуем в 0/1
      if (typeof value === 'boolean') {
        result[field] = value ? 1 : 0;
      }
    }
  });

  return result;
};

/** Вид прохода по предварительной записи (RecordPassVid) */
export const RECORD_PASS_VID = { ByAbon: 0, ByPayment: 1, ByPacket: 2 };

export const recordPassVidOptions = [
  { value: RECORD_PASS_VID.ByAbon, label: "Абонемент" },
  { value: RECORD_PASS_VID.ByPayment, label: "За плату" },
  { value: RECORD_PASS_VID.ByPacket, label: "За пакет" },
];

/** null на клиенте → значение записи; иначе ByAbon */
export const resolveRecordPassVid = (cntPassVid, recordPassVid) =>
  cntPassVid ?? recordPassVid ?? RECORD_PASS_VID.ByAbon;